import React, { useState, useEffect, useCallback } from 'react';
import {
  X,
  Calendar,
  User,
  Clock,
  CheckCircle,
  FileText,
  ExternalLink,
  MessageCircle, IndianRupeeIcon
} from 'lucide-react';
import api from '../../api';

const CollaborationDetailsModal = ({ collaborationId, isOpen, onClose }) => {
  const [collaboration, setCollaboration] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchCollaborationDetails = useCallback(async () => {
    setLoading(true);
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      
      // Try to fetch detailed collaboration data
      const response = await api.get(`/creators/${user.userId}/collaborations/${collaborationId}/details`);
      setCollaboration(response.data);
      
    } catch (error) {
      // Fallback: Try to get basic collaboration data from the list
      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const response = await api.get(`/creators/${user.userId}/collaborations`);
        const collaborationsData = response.data || [];
        const foundCollab = collaborationsData.find(c => c.id === collaborationId);
        
        if (foundCollab) {
          // Enhance basic collaboration data with additional fields for the modal
          const enhancedCollab = {
            ...foundCollab,
            requirements: foundCollab.requirements || [
              "Content must align with brand guidelines",
              "High-quality images and videos required",
              "Proper hashtag usage mandatory"
            ],
            deliverables: foundCollab.deliverables?.map(d => ({
              item: d,
              quantity: 1,
              status: "pending"
            })) || [
              { item: "Social Media Content", quantity: 1, status: "pending" }
            ],
            timeline: [
              { date: foundCollab.startDate, event: "Campaign Started", status: "completed" },
              { date: foundCollab.endDate, event: "Campaign Ends", status: "pending" }
            ],
            brandContact: {
              name: "Brand Manager",
              role: "Marketing Team",
              email: "contact@brand.com"
            },
            totalPayout: foundCollab.budget || 0,
            paidAmount: 0,
            pendingAmount: foundCollab.budget || 0
          };
          setCollaboration(enhancedCollab);
        } else {
          throw new Error('Collaboration not found');
        }
      } catch (fallbackError) {
        setCollaboration(null);
      }
    } finally {
      setLoading(false);
    }
  }, [collaborationId]);

  useEffect(() => {
    if (isOpen && collaborationId) {
      fetchCollaborationDetails();
    }
  }, [isOpen, collaborationId, fetchCollaborationDetails]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-100';
      case 'in_progress': return 'text-blue-600 bg-blue-100';
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle className="w-4 h-4" />;
      case 'in_progress': return <Clock className="w-4 h-4" />;
      case 'pending': return <Clock className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const formatINR = (value) => `₹${Number(value || 0).toLocaleString('en-IN')}`;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Collaboration Details</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : collaboration ? (
          <div className="p-6 space-y-6">
            {/* Campaign Overview */}
            <div className="bg-gray-50 rounded-lg p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{collaboration.campaignTitle}</h3>
                  <p className="text-gray-600">by {collaboration.brandName}</p>
                </div>
                <div className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(collaboration.status)}`}>
                  {getStatusIcon(collaboration.status)}
                  <span className="capitalize">{collaboration.status.replace('_', ' ')}</span>
                </div>
              </div>
              
              <p className="text-gray-700 mb-4">{collaboration.description}</p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <Calendar className="w-4 h-4" />
                  <span>{collaboration.startDate} - {collaboration.endDate}</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <IndianRupeeIcon className="w-4 h-4" />
                  <span>{formatINR(collaboration.budget)}</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <User className="w-4 h-4" />
                  <span>{collaboration.brandContact.name}</span>
                </div>
              </div>
            </div>

            {/* Progress */}
            <div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Campaign Progress</h4>
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-900">Overall Progress</span>
                  <span className="text-sm text-gray-600">{collaboration.progress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div 
                    className="bg-blue-600 h-3 rounded-full transition-all duration-300" 
                    style={{ width: `${collaboration.progress}%` }}
                  ></div>
                </div>
              </div>
            </div>

            {/* Deliverables */}
            <div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Deliverables</h4>
              <div className="space-y-3">
                {collaboration.deliverables.map((deliverable, index) => (
                  <div key={index} className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <FileText className="w-5 h-5 text-gray-400" />
                        <div>
                          <p className="font-medium text-gray-900">{deliverable.item}</p>
                          <p className="text-sm text-gray-600">Quantity: {deliverable.quantity}</p>
                        </div>
                      </div>
                      <div className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(deliverable.status)}`}>
                        {getStatusIcon(deliverable.status)}
                        <span className="capitalize">{deliverable.status.replace('_', ' ')}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Timeline */}
            <div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Timeline</h4>
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="space-y-3">
                  {collaboration.timeline.map((event, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <div className={`w-3 h-3 rounded-full ${event.status === 'completed' ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <p className="font-medium text-gray-900">{event.event}</p>
                          <p className="text-sm text-gray-600">{event.date}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Payment Information */}
            <div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Payment Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <p className="text-sm text-gray-600">Total Payout</p>
                  <p className="text-xl font-bold text-gray-900">{formatINR(collaboration.totalPayout)}</p>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <p className="text-sm text-gray-600">Paid Amount</p>
                  <p className="text-xl font-bold text-green-600">{formatINR(collaboration.paidAmount)}</p>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <p className="text-sm text-gray-600">Pending Amount</p>
                  <p className="text-xl font-bold text-yellow-600">{formatINR(collaboration.pendingAmount)}</p>
                </div>
              </div>
            </div>

            {/* Brand Contact */}
            <div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Brand Contact</h4>
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">{collaboration.brandContact.name}</p>
                    <p className="text-sm text-gray-600">{collaboration.brandContact.role}</p>
                    <p className="text-sm text-gray-600">{collaboration.brandContact.email}</p>
                  </div>
                  <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2">
                    <MessageCircle className="w-4 h-4" />
                    <span>Send Message</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4 pt-6 border-t border-gray-200">
              <button className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2">
                <ExternalLink className="w-5 h-5" />
                <span>Submit Content</span>
              </button>
              <button className="border border-gray-300 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-50 transition-colors">
                Download Brief
              </button>
              <button className="border border-gray-300 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-50 transition-colors">
                Report Issue
              </button>
            </div>
          </div>
        ) : (
          <div className="p-6 text-center">
            <p className="text-gray-600">Failed to load collaboration details.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CollaborationDetailsModal;
